/** @file shownTileOf: the facet entry and tile the viewport draws for a part now, as a Tile tab address. */
import type { ResolvedEntry } from '@zilo/pose3d-export/types/bundle';
import { nodeAngles } from '@zilo/pose3d-facet/azimuth/nodeAngles';
import { sectorPick, type SectorPickOut } from '@zilo/pose3d-facet/sector/sectorPick';
import { resolveSectorTable } from '@zilo/pose3d-format/tables/resolveSectorTable';
import type { FacetEntry } from '@zilo/pose3d-format/types/facet';
import { IMPOSTOR_LAYOUT as L } from '@zilo/pose3d-render-core/layout/impostorLayout';
import type { TileRef } from '../../state/editorTypes';
import { docEntryOf } from './docEntryOf';
import type { PreviewPuppet } from './previewPuppet';
import { rowOfDrawable } from './rowOfDrawable';
import type { ViewportCtx } from './viewportCtx';

type Rect = readonly [number, number, number, number];

const same = (a: Rect, b: Rect): boolean => a[0] === b[0] && a[1] === b[1] && a[2] === b[2] && a[3] === b[3];

/** The `{ entry, tile }` whose bundle rect is the drawn row's sheet rect, or null (no row for the drawable). */
function byRect(p: PreviewPuppet, facets: readonly ResolvedEntry[], drawableIndex: number): { entry: number; tile: number } | null {
  const { list, count } = p.instance.state();
  const i = rowOfDrawable(list, count, drawableIndex);
  if (i < 0) return null;
  const at = (k: number): number => list[i * L.stride + k] ?? 0;
  const sheet = p.bundle.sheetNames[at(L.sheet)];
  const rect: Rect = [at(L.texX), at(L.texY), at(L.texW), at(L.texH)];
  for (const [entry, e] of facets.entries()) {
    const tile = e.sheet === sheet ? e.rects.findIndex((r) => r !== null && same(r, rect)) : -1;
    if (tile >= 0) return { entry, tile };
  }
  return null;
}

/** The facet pick's entry for drawable `id` (the runtime's own lookup), else 0. */
function pickedEntry(p: PreviewPuppet, id: string): number {
  const entry = p.instance.state().picks.find((k) => k.drawable === id)?.entry ?? -1;
  return entry >= 0 ? entry : 0;
}

/** The tile the document entry's sector table gives at the node's azimuth (body facing + node yaw + camera yaw); -1 none. */
function bySector(ctx: ViewportCtx, p: PreviewPuppet, entry: FacetEntry, node: string): number {
  const nodeIndex = p.names.indexOf(node);
  const table = p.bundle.puppet.tables[entry.yaw] ?? resolveSectorTable(entry.yaw, ctx.store.getState().puppet.doc?.puppet ?? {});
  if (nodeIndex < 0 || table === undefined || ctx.scene === null) return -1;
  const state = p.instance.state();
  const { azimuthDeg } = nodeAngles(state.pose, nodeIndex, ctx.scene.camera.contract().yawDeg, { azimuthDeg: 0, pitchDeg: 0 });
  const out: SectorPickOut = { sector: 0, tile: -1, flip: false, sideBack: 0, drawnAzimuthDeg: 0, drawnScreenAngleDeg: 0 };
  return sectorPick(table, entry, azimuthDeg, out).tile;
}

/**
 * Drawable `drawableIndex` of the drawn puppet: the impostor row's sheet and uncropped `texX texY texW texH` matched
 * against the drawable's entries' tile rects. The rects are the preview bundle's (`ResolvedEntry.rects`), the ones the
 * runtime copies into the row: the bundle repacks the document's sheets, so the document's `entryRect` would not
 * match. No row (the part draws nothing at this angle, e.g. an empty slot): `sectorPick` on the facet pick's entry
 * (entry 0 without one) of the document at the node's azimuth. Null for a non-impostor, an unknown index or tile -1.
 */
export function shownTileOf(ctx: ViewportCtx, drawableIndex: number): TileRef | null {
  const p = ctx.puppet();
  const d = p?.bundle.puppet.drawables[drawableIndex];
  if (p === null || d?.kind !== 'impostor') return null;
  const hit = byRect(p, d.facets, drawableIndex);
  if (hit !== null) return { drawable: d.id, ...hit };
  const entry = pickedEntry(p, d.id);
  const e = docEntryOf(ctx.store, { drawable: d.id, entry });
  const tile = e === null ? -1 : bySector(ctx, p, e, d.node);
  return tile < 0 ? null : { drawable: d.id, entry, tile };
}
